import { z } from 'zod';

// Governed vocabulary for the member_profiles domain (verified legal identity,
// 1:1 with members). NOT the `member-profile` feature dir — that is the
// onboarding-answers-derived member-facing view; this vocabulary backs the
// canonical PII table Atlas reads.
//
// The `as const` array drives BOTH the `text`+CHECK constraint rendered in the
// DB schema and the wire/contract schema here, so the DB and the validators
// cannot drift (ADR-056 Rule 8; mirrors eligibility/schema.ts).

// ─── member_profiles provenance ─────────────────────────────────────────────
// Provenance of the row's IDENTITY content — which writer last supplied the
// legal-identity values. `device` marks a capture-only stub with no verified
// identity yet; it currently has no writer (the device-capture slice is
// deferred behind the ADR-095 timezone model) but stays in the vocabulary
// because the applied CHECK constraint already names it. A closed vocabulary
// that grows with later writer slices (Healthie provisioning, member
// self-edit), each landing with its own source value. `backend2_import` marks
// a row whose legal identity was carried over from the member's Pelago 1.0
// record by the migration import (`source_ref` names the legacy row).
export const MEMBER_PROFILE_SOURCE = ['eligibility_check', 'device', 'backend2_import'] as const;
export const memberProfileSourceSchema = z.enum(MEMBER_PROFILE_SOURCE);
export type MemberProfileSource = z.infer<typeof memberProfileSourceSchema>;

// ─── member identity details ─────────────────────────────────────────────────
// Upper bound for the free-text identity details a member gives us (gender
// identity, pronouns, preferred name). These are deliberately OPEN strings, not
// a governed vocabulary: Pelago 1.0 stored them as member-typed varchar(50) and
// identity vocabulary stays unconstrained by design. The bound exists so the
// column is replication-safe (a `text` column needs a length CHECK) and so the
// wire cannot accept an essay where a name belongs; 100 leaves room for a
// hyphenated or multi-part name. One constant drives BOTH the DB CHECK and the
// wire `.max()`, so the two bounds cannot drift.
export const MEMBER_PROFILE_IDENTITY_TEXT_MAX = 100;

// The values a client stores for a preset pick of gender identity or pronouns:
// Pelago 1.0 signup's option keys, in its display order. 1.0 sent these keys
// (or the member's own self-described words) to backend2, and the legacy import
// copies them verbatim, so a new signup and an imported one hold the same
// vocabulary whatever language the member signed up in. Descriptive only: the
// columns stay open text and nothing on the server validates against these.
export const MEMBER_GENDER_IDENTITY_OPTIONS = [
  'none',
  'cisWoman',
  'cisMan',
  'transwoman',
  'transman',
  'nonBinary',
  'genderqueer',
] as const;
export type MemberGenderIdentityOption = (typeof MEMBER_GENDER_IDENTITY_OPTIONS)[number];

export const MEMBER_PRONOUN_OPTIONS = ['none', 'theyThem', 'sheHer', 'heHim', 'zeZir'] as const;
export type MemberPronounOption = (typeof MEMBER_PRONOUN_OPTIONS)[number];
